import { supabase } from '../../lib/supabaseClient';
import type { Database } from '../../types/database.types';

export const DEFAULT_AI_AGENT_PORTRAIT = '/nova-stellaris.png';

export const DEFAULT_AI_AGENT_MODEL = 'gemini-3.5-flash-lite';

export const AI_AGENT_MODELS = [
  { id: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash-Lite' },
  { id: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
  { id: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash' },
] as const;

export const AI_AGENT_REASONING = [
  { id: 'minimale', label: 'Minimale' },
  { id: 'faible', label: 'Faible' },
  { id: 'moyenne', label: 'Moyenne' },
  { id: 'élevée', label: 'Élevée' },
] as const;

export type AiAgentGender = 'female' | 'male';
export type AiAgentModel = (typeof AI_AGENT_MODELS)[number]['id'];
export type AiAgentReasoning = (typeof AI_AGENT_REASONING)[number]['id'];

export type AiAgentSettings = {
  enabled: boolean;
  name: string;
  gender: AiAgentGender;
  photoUrl: string | null;
  model: AiAgentModel;
  reasoning: AiAgentReasoning;
};

const MODEL_IDS = new Set<string>(AI_AGENT_MODELS.map((m) => m.id));
const REASONING_IDS = new Set<string>(AI_AGENT_REASONING.map((m) => m.id));

const SELECT =
  'ai_agent_enabled, ai_agent_name, ai_agent_gender, ai_agent_photo_url, ai_agent_model, ai_agent_reasoning';

export function parseAiAgentSettings(row: {
  ai_agent_enabled?: unknown;
  ai_agent_name?: unknown;
  ai_agent_gender?: unknown;
  ai_agent_photo_url?: unknown;
  ai_agent_model?: unknown;
  ai_agent_reasoning?: unknown;
} | null | undefined): AiAgentSettings {
  const model = typeof row?.ai_agent_model === 'string' ? row.ai_agent_model : '';
  const reasoning = typeof row?.ai_agent_reasoning === 'string' ? row.ai_agent_reasoning : '';
  const name = typeof row?.ai_agent_name === 'string' ? row.ai_agent_name.trim() : '';
  return {
    enabled: row?.ai_agent_enabled === true,
    name: name || 'Nova Stellaris',
    gender: row?.ai_agent_gender === 'male' ? 'male' : 'female',
    photoUrl: typeof row?.ai_agent_photo_url === 'string' && row.ai_agent_photo_url
      ? row.ai_agent_photo_url
      : null,
    model: MODEL_IDS.has(model) ? (model as AiAgentModel) : DEFAULT_AI_AGENT_MODEL,
    reasoning: REASONING_IDS.has(reasoning) ? (reasoning as AiAgentReasoning) : 'moyenne',
  };
}

export function aiAgentPortraitSrc(photoUrl: string | null): string {
  return photoUrl || DEFAULT_AI_AGENT_PORTRAIT;
}

export async function getAiAgentSettings(): Promise<AiAgentSettings> {
  const { data, error } = await supabase
    .from('cabinet_settings')
    .select(SELECT)
    .eq('id', 1)
    .maybeSingle();
  if (error) throw new Error(`Failed to read AI agent settings: ${error.message}`);
  return parseAiAgentSettings(data);
}

export async function updateAiAgentSettings(
  input: Pick<AiAgentSettings, 'enabled' | 'name' | 'gender' | 'model' | 'reasoning'>,
  updatedBy?: string | null,
): Promise<AiAgentSettings> {
  const name = input.name.trim();
  if (!name) throw new Error('Le nom est obligatoire.');
  const patch: Database['public']['Tables']['cabinet_settings']['Update'] = {
    ai_agent_enabled: input.enabled,
    ai_agent_name: name,
    ai_agent_gender: input.gender,
    ai_agent_model: input.model,
    ai_agent_reasoning: input.reasoning,
    updated_by: updatedBy ?? null,
  };
  const { data, error } = await supabase
    .from('cabinet_settings')
    .update(patch)
    .eq('id', 1)
    .select(SELECT)
    .single();
  if (error) throw new Error(`Failed to update AI agent settings: ${error.message}`);
  return parseAiAgentSettings(data);
}

export async function setAiAgentEnabled(
  enabled: boolean,
  updatedBy?: string | null,
): Promise<AiAgentSettings> {
  const patch: Database['public']['Tables']['cabinet_settings']['Update'] = {
    ai_agent_enabled: enabled,
    updated_by: updatedBy ?? null,
  };
  const { data, error } = await supabase
    .from('cabinet_settings')
    .update(patch)
    .eq('id', 1)
    .select(SELECT)
    .single();
  if (error) throw new Error(`Failed to update AI agent settings: ${error.message}`);
  return parseAiAgentSettings(data);
}

export async function uploadAiAgentPortrait(
  file: File,
  updatedBy?: string | null,
): Promise<string> {
  const path = `cime/ai-agent-portrait-${Date.now()}.jpg`;
  const { error: uploadError } = await supabase.storage
    .from('branding')
    .upload(path, file, { contentType: 'image/jpeg', upsert: false, cacheControl: '3600' });
  if (uploadError) throw new Error(uploadError.message);
  const { data: pub } = supabase.storage.from('branding').getPublicUrl(path);
  const photoUrl = pub.publicUrl;
  const patch: Database['public']['Tables']['cabinet_settings']['Update'] = {
    ai_agent_photo_url: photoUrl,
    updated_by: updatedBy ?? null,
  };
  const { error } = await supabase
    .from('cabinet_settings')
    .update(patch)
    .eq('id', 1);
  if (error) throw new Error(`Failed to save the portrait: ${error.message}`);
  return photoUrl;
}
